import { api } from "@/lib/apiClient";
import type { AbsenceStatus, AbsenceType, HalfDayPart } from "@/lib/employeeAbsences";

export type PlanungRowKind = "employee" | "resource" | "separator";

export interface PlanungRow {
  id: number;
  kind: PlanungRowKind;
  employee_login: string | null;
  label: string | null;
  color: string | null;
  sort_order: number;
  active: boolean;
  /** ISO-Wochentage (1=Mo … 7=So) ohne Arbeit laut Zeitmodell; nur bei Mitarbeiter-Zeilen. */
  non_work_weekdays?: number[] | null;
}

export interface PlanungOrderLink {
  order_id: number;
  order_label: string | null;
}

export interface PlanungFeinplanungEditor {
  employee_login: string;
  employee_display?: string | null;
}

export interface PlanungDayNote {
  work_date: string;
  note: string;
}

export interface PlanungAssignment {
  id: number;
  row_id: number;
  title: string;
  color: string | null;
  date_from: string;
  date_to: string;
  is_fixed: boolean;
  is_blocker?: boolean;
  is_company_event?: boolean;
  event_start_time?: string | null;
  event_address?: string | null;
  orders: PlanungOrderLink[];
  note: string | null;
  /** Tagesnotizen der Feinplanung, nur für diesen Einsatz. */
  day_notes?: PlanungDayNote[];
  link_group_id?: number | null;
  linked_assignment_ids?: number[];
  /** Verknüpfte Termine bleiben verbunden; Projekte gelten nur für diesen Einsatz. */
  is_series?: boolean;
  can_edit_feinplanung?: boolean;
  can_request_feinplanung?: boolean;
  feinplanung_request_pending?: boolean;
  open_feinplanung_request?: PlanungFeinplanungRequestSummary | null;
  feinplanung_editors?: PlanungFeinplanungEditor[];
  /** Feinplanung für den angezeigten Tag (nur in Mein Tag). */
  feinplanung_day?: PlanungDayOrders | null;
  monteur_proposal_status?: MonteurProposalStatus | null;
  monteur_proposal_by_login?: string | null;
  monteur_proposal_at?: string | null;
  can_edit_monteur_proposal?: boolean;
}

export interface PlanungAssignmentLinkPeer {
  id: number;
  row_id: number;
  title: string;
  date_from: string;
  date_to: string;
}

export interface PlanungAbsence {
  id: number;
  user_login: string;
  user_display: string | null;
  absence_type: AbsenceType;
  status: AbsenceStatus;
  date_from: string;
  date_to: string;
  day_fraction: number;
  half_day_part: HalfDayPart | null;
  from_hero?: boolean;
  from_bridge?: boolean;
  from_betriebsferien?: boolean;
  /** Urlaubstag, der durch Krankheit überlagert und visuell ausgegraut wird. */
  vacation_credited?: boolean;
}

export interface PlanungCalendarDay {
  work_date: string;
  kind: "holiday" | "bridge" | "betriebsferien";
  label: string;
}

/** Büro-eingetragene Nicht-Verfügbarkeit (ohne Abwesenheitsantrag). */
export interface PlanungUnavailability {
  id: number;
  row_id: number;
  date_from: string;
  date_to: string;
  note: string | null;
}

export interface PlanungUnavailabilityInput {
  row_id: number;
  date_from: string;
  date_to: string;
  note?: string | null;
}

export interface PlanungBoard {
  date_from: string;
  date_to: string;
  can_edit: boolean;
  can_view_pending_absences?: boolean;
  can_approve_absences?: boolean;
  can_view_work_hours?: boolean;
  /** Ablage mit ungeplanten Montagen, nur für die Gruppe Büro. */
  can_view_ablage?: boolean;
  can_approve_feinplanung_requests?: boolean;
  can_create_monteur_proposal?: boolean;
  can_approve_monteur_proposals?: boolean;
  viewer_login?: string;
  viewer_row_id?: number | null;
  bundesland: string;
  rows: PlanungRow[];
  assignments: PlanungAssignment[];
  absences: PlanungAbsence[];
  unavailabilities?: PlanungUnavailability[];
  calendar_days: PlanungCalendarDay[];
  is_historical?: boolean;
  historical_log_id?: number | null;
  historical_at?: string | null;
  historical_summary?: string | null;
  historical_by_login?: string | null;
  historical_by_display?: string | null;
}

export interface PlanungChangeLogEntry {
  id: number;
  created_at: string;
  changed_by_login: string;
  changed_by_display: string | null;
  action: string;
  summary: string;
  entity_type: string | null;
  entity_id: number | null;
  /** Konkrete Feldänderungen, z. B. Zeitraum oder Zeile. */
  details?: string[];
}

export interface PlanungChangeLogList {
  items: PlanungChangeLogEntry[];
  total: number;
}

export interface PlanungAssignmentInput {
  row_id: number;
  title: string;
  color?: string | null;
  date_from: string;
  date_to: string;
  is_fixed?: boolean;
  is_blocker?: boolean;
  is_company_event?: boolean;
  orders?: PlanungOrderLink[];
  note?: string | null;
  link_to_assignment_id?: number | null;
}

export interface PlanungDayOrders {
  work_date: string;
  orders: PlanungOrderLink[];
  note?: string | null;
}

/** Ein Termin einer Serie, mit der Feinplanung nur dieses Zeitraums. */
export interface PlanungFeinplanungMember {
  id: number;
  date_from: string;
  date_to: string;
  title: string;
  days: PlanungDayOrders[];
}

export interface PlanungFeinplanung {
  assignment_id: number;
  title: string;
  date_from: string;
  date_to: string;
  assignment_orders: PlanungOrderLink[];
  /** Projekte nur dieses Einsatzes (bei einer Serie nicht die der anderen Termine). */
  focused_assignment_orders?: PlanungOrderLink[];
  days: PlanungDayOrders[];
  can_edit?: boolean;
  can_manage_delegates?: boolean;
  feinplanung_editors?: PlanungFeinplanungEditor[];
  /** Weitere Einsätze in derselben Verknüpfung (Feinplanung gemeinsam). */
  linked_assignment_ids?: number[];
  /** Serie: jeder Einsatz hat seine eigene Feinplanung, Nachbartermine sind einzeln abrufbar. */
  series_mode?: boolean;
  series_members?: PlanungFeinplanungMember[];
}

export interface PlanungFeinplanungInput {
  days: PlanungDayOrders[];
}

export type MonteurProposalStatus = "pending" | "approved" | "rejected";

export interface PlanungMonteurProposalInput {
  row_id: number;
  title: string;
  color?: string | null;
  date_from: string;
  date_to: string;
  is_fixed?: boolean;
  is_blocker?: boolean;
  note?: string | null;
  /** false: Antrag nur für Kollegen oder Ressourcen, der Antragsteller bleibt außen vor. */
  include_self?: boolean;
  invite_employee_logins?: string[];
  /** Weitere Ressourcenzeilen, zusätzlich zur angeklickten Zeile. */
  resource_row_ids?: number[];
  orders?: PlanungOrderLink[];
}

export interface PlanungMonteurProposalInvitee {
  employee_login: string;
  employee_display?: string | null;
  row_label?: string | null;
  kind?: string | null;
}

export interface PlanungMonteurProposalListItem {
  id: number;
  row_id: number;
  row_label?: string | null;
  row_kind?: string | null;
  title: string;
  date_from: string;
  date_to: string;
  employee_login: string;
  employee_display?: string | null;
  requested_at: string;
  /** false, wenn der Antragsteller selbst nicht eingeplant ist. */
  requester_included?: boolean;
  invitees?: PlanungMonteurProposalInvitee[];
  orders?: PlanungOrderLink[];
}

export interface PlanungFeinplanungRequestSummary {
  id: number;
  employee_login: string;
  employee_display?: string | null;
  requested_at: string;
}

export interface PlanungFeinplanungRequest {
  id: number;
  assignment_id: number;
  assignment_row_id?: number;
  assignment_title: string;
  assignment_date_from: string;
  assignment_date_to: string;
  row_label?: string | null;
  employee_login: string;
  employee_display?: string | null;
  requested_at: string;
  status: string;
}

export interface PlanungFeinplanungRequestStatus {
  status: string;
  assignment_id: number;
}

export interface PlanungCompanyEventInput {
  title: string;
  date_from: string;
  date_to: string;
  event_start_time?: string | null;
  event_address?: string | null;
  note?: string | null;
  orders?: PlanungOrderLink[];
  row_ids?: number[];
}

export interface PlanungCompanyEventResult {
  assignment_ids: number[];
  participant_count: number;
}

export interface PlanungRowInput {
  kind: PlanungRowKind;
  employee_login?: string | null;
  label?: string | null;
  color?: string | null;
  sort_order?: number | null;
}

export interface PlanungRowUpdate {
  label?: string | null;
  color?: string | null;
  active?: boolean;
  sort_order?: number;
}

/** Vordefinierte Farben für Einsatz-Balken und Ressourcen-Zeilen. */
export const PLANUNG_COLORS: Array<{ value: string; label: string }> = [
  { value: "#0a84ff", label: "Blau" },
  { value: "#30b0c7", label: "Türkis" },
  { value: "#34c759", label: "Grün" },
  { value: "#ffd60a", label: "Gelb" },
  { value: "#ff9f0a", label: "Orange" },
  { value: "#ff375f", label: "Rot" },
  { value: "#bf5af2", label: "Violett" },
  { value: "#8e8e93", label: "Grau" },
];

export const DEFAULT_PLANUNG_COLOR = PLANUNG_COLORS[0]!.value;

/** Standardfarbe für Betriebsveranstaltungen in der Montageplanung. */
export const COMPANY_EVENT_COLOR = "#ff9f0a";

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

export function getBoard(params: { date_from: string; date_to: string }): Promise<PlanungBoard> {
  return api.get(`/montageplanung/board${buildQuery(params)}`);
}

export interface PlanungOrderScheduleItem {
  assignment_id: number;
  row_id: number;
  who: string;
  title: string;
  date_from: string;
  date_to: string;
  day_count: number;
}

export interface PlanungOrderSchedule {
  order_id: number;
  items: PlanungOrderScheduleItem[];
}

export function getOrderMontageSchedule(orderId: number): Promise<PlanungOrderSchedule> {
  return api.get(`/montageplanung/orders/${orderId}/schedule`);
}

export function montageplanHighlightPath(item: {
  assignment_id: number;
  date_from: string;
  date_to: string;
}): string {
  const params = new URLSearchParams({
    highlight: String(item.assignment_id),
    from: item.date_from.slice(0, 10),
    to: item.date_to.slice(0, 10),
  });
  return `/montageplanung?${params.toString()}`;
}

export function parseMontageplanHighlight(
  highlight: string | null,
  from: string | null,
  to: string | null,
): { assignmentId: number; dateFrom: string; dateTo: string; rowId: number } | null {
  const id = Number(highlight);
  const dateFrom = (from || "").slice(0, 10);
  const dateTo = (to || dateFrom).slice(0, 10);
  if (!Number.isFinite(id) || id <= 0) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateFrom) || !/^\d{4}-\d{2}-\d{2}$/.test(dateTo)) return null;
  return { assignmentId: id, dateFrom, dateTo, rowId: 0 };
}

export function formatMontageScheduleWhen(
  dateFrom: string,
  dateTo: string,
  dayCount: number,
): string {
  const fmt = (iso: string) => {
    const [y, m, d] = iso.slice(0, 10).split("-");
    if (!y || !m || !d) return iso;
    return `${d}.${m}.${y}`;
  };
  const start = fmt(dateFrom);
  const end = fmt(dateTo);
  const span = start === end ? start : `${start} – ${end}`;
  const days = Number.isFinite(dayCount) && dayCount > 0 ? Math.trunc(dayCount) : 1;
  const duration = days === 1 ? "1 Tag" : `${days} Tage`;
  return `${span} · ${duration}`;
}

export type MontageScheduleTimingTone = "today" | "upcoming" | "past";

export interface MontageScheduleTiming {
  label: string;
  tone: MontageScheduleTimingTone;
}

function isoDayIndex(iso: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

/** Relativer Hinweis zum geplanten Zeitraum: heute, morgen, in N Tagen, gestern, vor N Tagen. */
export function montageScheduleTiming(
  dateFrom: string,
  dateTo: string,
  now: Date = new Date(),
): MontageScheduleTiming | null {
  const start = isoDayIndex(dateFrom);
  const end = isoDayIndex(dateTo) ?? start;
  if (start == null || end == null) return null;
  const today = Math.floor(
    Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / 86_400_000,
  );
  const first = Math.min(start, end);
  const last = Math.max(start, end);
  if (today >= first && today <= last) return { label: "heute", tone: "today" };
  if (today < first) {
    const days = first - today;
    return {
      label: days === 1 ? "morgen" : `in ${days} Tagen`,
      tone: "upcoming",
    };
  }
  const days = today - last;
  return {
    label: days === 1 ? "gestern" : `vor ${days} Tagen`,
    tone: "past",
  };
}

export interface PlanungMyDay {
  date: string;
  assignments: PlanungAssignment[];
  absence: PlanungAbsence | null;
}

export function getMyDay(date?: string): Promise<PlanungMyDay> {
  return api.get(`/montageplanung/my-day${buildQuery({ date })}`);
}

export function getChangeLog(params?: {
  limit?: number;
  offset?: number;
  entity_type?: string;
  entity_ids?: string;
  q?: string;
}): Promise<PlanungChangeLogList> {
  return api.get(`/montageplanung/history${buildQuery(params ?? {})}`);
}

export function getHistoryBoard(
  logId: number,
  params: { date_from: string; date_to: string },
): Promise<PlanungBoard> {
  return api.get(`/montageplanung/history/${logId}/board${buildQuery(params)}`);
}

export function createAssignment(payload: PlanungAssignmentInput): Promise<PlanungAssignment> {
  return api.post("/montageplanung/assignments", payload);
}

export function createCompanyEvent(
  payload: PlanungCompanyEventInput,
): Promise<PlanungCompanyEventResult> {
  return api.post("/montageplanung/company-events", payload);
}

export function updateCompanyEvent(
  assignmentId: number,
  payload: PlanungCompanyEventInput,
): Promise<PlanungCompanyEventResult> {
  return api.put(`/montageplanung/company-events/${assignmentId}`, payload);
}

export function deleteCompanyEvent(
  assignmentId: number,
): Promise<{ status: string; ids: number[] }> {
  return api.del(`/montageplanung/company-events/${assignmentId}`);
}

export function updateAssignment(
  id: number,
  payload: PlanungAssignmentInput,
): Promise<PlanungAssignment> {
  return api.put(`/montageplanung/assignments/${id}`, payload);
}

export function deleteAssignment(id: number): Promise<{ status: string; id: number }> {
  return api.del(`/montageplanung/assignments/${id}`);
}

export function getAssignmentLinks(assignmentId: number): Promise<PlanungAssignmentLinkPeer[]> {
  return api.get(`/montageplanung/assignments/${assignmentId}/links`);
}

export interface PlanungAssignmentLinkCompareItem {
  id: number;
  row_id: number;
  title: string;
  date_from: string;
  date_to: string;
}

export interface PlanungAssignmentLinkDiff {
  field: string;
  label: string;
  values: Record<string, string>;
}

export interface PlanungAssignmentLinkCompare {
  assignments: PlanungAssignmentLinkCompareItem[];
  has_differences: boolean;
  differing_fields: PlanungAssignmentLinkDiff[];
}

export function compareAssignmentLinks(
  assignmentId: number,
  assignmentIds: number[],
): Promise<PlanungAssignmentLinkCompare> {
  return api.post(`/montageplanung/assignments/${assignmentId}/links/compare`, {
    assignment_ids: assignmentIds,
  });
}

export function updateAssignmentLinks(
  assignmentId: number,
  assignmentIds: number[],
  contentSourceAssignmentId?: number,
): Promise<PlanungAssignmentLinkPeer[]> {
  return api.put(`/montageplanung/assignments/${assignmentId}/links`, {
    assignment_ids: assignmentIds,
    content_source_assignment_id: contentSourceAssignmentId ?? undefined,
  });
}

export function unlinkAssignment(assignmentId: number): Promise<{ status: string; id: number }> {
  return api.del(`/montageplanung/assignments/${assignmentId}/links`);
}

export function updateAssignmentSeriesMode(
  assignmentId: number,
  enabled: boolean,
): Promise<{ series_mode: boolean; assignment_ids: number[]; orders_localized: boolean }> {
  return api.put(`/montageplanung/assignments/${assignmentId}/series`, { enabled });
}

export function getAssignmentFeinplanung(assignmentId: number): Promise<PlanungFeinplanung> {
  return api.get(`/montageplanung/assignments/${assignmentId}/feinplanung`);
}

export function updateAssignmentFeinplanung(
  assignmentId: number,
  payload: PlanungFeinplanungInput,
): Promise<PlanungFeinplanung> {
  return api.put(`/montageplanung/assignments/${assignmentId}/feinplanung`, payload);
}

export function updateAssignmentFeinplanungEditors(
  assignmentId: number,
  employeeLogins: string[],
): Promise<PlanungFeinplanungEditor[]> {
  return api.put(`/montageplanung/assignments/${assignmentId}/feinplanung/editors`, {
    employee_logins: employeeLogins,
  });
}

export function requestAssignmentFeinplanung(
  assignmentId: number,
): Promise<PlanungFeinplanungRequestStatus> {
  return api.post(`/montageplanung/assignments/${assignmentId}/feinplanung/request`, {});
}

export function fetchPendingFeinplanungRequests(): Promise<PlanungFeinplanungRequest[]> {
  return api.get("/montageplanung/feinplanung/requests/pending");
}

export function fetchFeinplanungRequestsOpenCount(): Promise<number> {
  return api.get<{ count?: number }>("/montageplanung/feinplanung/requests/open-count").then(
    (data) => Number(data?.count || 0),
  );
}

export function approveFeinplanungRequest(
  requestId: number,
): Promise<PlanungFeinplanungRequest> {
  return api.post(`/montageplanung/feinplanung/requests/${requestId}/approve`, {});
}

export function rejectFeinplanungRequest(
  requestId: number,
): Promise<PlanungFeinplanungRequest> {
  return api.post(`/montageplanung/feinplanung/requests/${requestId}/reject`, {});
}

export function createMonteurProposal(
  payload: PlanungMonteurProposalInput,
): Promise<PlanungAssignment> {
  return api.post("/montageplanung/assignments/monteur-proposal", payload);
}

export function updateMonteurProposal(
  assignmentId: number,
  payload: PlanungMonteurProposalInput,
): Promise<PlanungAssignment> {
  return api.put(`/montageplanung/assignments/${assignmentId}/monteur-proposal`, payload);
}

export function deleteMonteurProposal(
  assignmentId: number,
): Promise<{ status: string; id: number }> {
  return api.del(`/montageplanung/assignments/${assignmentId}/monteur-proposal`);
}

export function fetchPendingMonteurProposals(): Promise<PlanungMonteurProposalListItem[]> {
  return api.get("/montageplanung/monteur-proposals/pending");
}

export function fetchMonteurProposalsOpenCount(): Promise<number> {
  return api.get<{ count?: number }>("/montageplanung/monteur-proposals/open-count").then(
    (data) => Number(data?.count || 0),
  );
}

export function approveMonteurProposal(assignmentId: number): Promise<PlanungAssignment> {
  return api.post(`/montageplanung/assignments/${assignmentId}/monteur-proposal/approve`, {});
}

export function rejectMonteurProposal(assignmentId: number): Promise<PlanungAssignment> {
  return api.post(`/montageplanung/assignments/${assignmentId}/monteur-proposal/reject`, {});
}

export function createRow(payload: PlanungRowInput): Promise<PlanungRow> {
  return api.post("/montageplanung/rows", payload);
}

export function updateRow(id: number, payload: PlanungRowUpdate): Promise<PlanungRow> {
  return api.put(`/montageplanung/rows/${id}`, payload);
}

export function deleteRow(id: number): Promise<{ status: string; id: number }> {
  return api.del(`/montageplanung/rows/${id}`);
}

export function reorderRows(ids: number[]): Promise<PlanungRow[]> {
  return api.put("/montageplanung/rows/reorder", { ids });
}

export function createUnavailability(
  payload: PlanungUnavailabilityInput,
): Promise<PlanungUnavailability> {
  return api.post("/montageplanung/unavailabilities", payload);
}

export function updateUnavailability(
  id: number,
  payload: PlanungUnavailabilityInput,
): Promise<PlanungUnavailability> {
  return api.put(`/montageplanung/unavailabilities/${id}`, payload);
}

export function deleteUnavailability(id: number): Promise<{ status: string; id: number }> {
  return api.del(`/montageplanung/unavailabilities/${id}`);
}

/** Personen, die an diesem Tag im Montageplan als nicht verfügbar markiert sind. */
export interface UnavailableOnDayPerson {
  id: number;
  user_login: string;
  user_display: string;
  date_from: string;
  date_to: string;
  note: string | null;
}

export function listUnavailableOnDay(on: string): Promise<UnavailableOnDayPerson[]> {
  return api.get(`/montageplanung/unavailabilities/on-day?on=${encodeURIComponent(on)}`);
}

/** Montage in der Ablage: Material ist da, Einplanung steht noch aus. */
export interface PlanungAblageItem {
  id: number;
  title: string;
  person_count: number;
  day_count: number;
  color: string | null;
  is_fixed: boolean;
  is_blocker: boolean;
  note: string | null;
  orders: PlanungOrderLink[];
  created_at: string;
}

export interface PlanungAblageInput {
  title: string;
  person_count: number;
  day_count: number;
  color?: string | null;
  is_fixed?: boolean;
  is_blocker?: boolean;
  note?: string | null;
  orders?: PlanungOrderLink[];
}

export interface PlanungAblageScheduleInput {
  row_ids: number[];
  date_from: string;
  date_to: string;
}

export function listAblage(): Promise<PlanungAblageItem[]> {
  return api.get("/montageplanung/ablage");
}

export function createAblage(payload: PlanungAblageInput): Promise<PlanungAblageItem> {
  return api.post("/montageplanung/ablage", payload);
}

export function updateAblage(
  id: number,
  payload: PlanungAblageInput,
): Promise<PlanungAblageItem> {
  return api.put(`/montageplanung/ablage/${id}`, payload);
}

export function deleteAblage(id: number): Promise<{ status: string; id: number }> {
  return api.del(`/montageplanung/ablage/${id}`);
}

export function scheduleAblage(
  id: number,
  payload: PlanungAblageScheduleInput,
): Promise<{ assignment_ids: number[]; ablage_id: number }> {
  return api.post(`/montageplanung/ablage/${id}/schedule`, payload);
}

export function parkAssignmentInAblage(assignmentId: number): Promise<PlanungAblageItem> {
  return api.post("/montageplanung/ablage/from-assignment", { assignment_id: assignmentId });
}
